import { useCallback, useEffect, useState } from 'react';

import { AdminTrustCenter } from './components/AdminTrustCenter';
import { AuthPanel } from './components/AuthPanel';
import { BattlesArena } from './components/BattlesArena';
import { MonthlyCupArena } from './components/MonthlyCupArena';
import { NotificationCenter } from './components/NotificationCenter';
import { ProfilePanel } from './components/ProfilePanel';
import { RankingBoard } from './components/RankingBoard';
import { ResetPassword } from './components/ResetPassword';
import { SponsorshipAdmin } from './components/SponsorshipAdmin';
import { authClient } from './lib/auth-client';
import type { MyAccount, PublicConfig } from './types';

export function App() {
  const { data: session, isPending, refetch } = authClient.useSession();
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [account, setAccount] = useState<MyAccount | null>(null);
  const [accountLoaded, setAccountLoaded] = useState(false);

  const loadAccount = useCallback(async () => {
    await Promise.resolve();
    if (!session) {
      setAccount(null);
      setAccountLoaded(true);
      return;
    }

    setAccountLoaded(false);
    const response = await fetch('/api/v1/me', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    setAccount(response.ok ? ((await response.json()) as MyAccount) : null);
    setAccountLoaded(true);
  }, [session]);

  useEffect(() => {
    void fetch('/api/v1/config/public')
      .then((response) => response.json() as Promise<PublicConfig>)
      .then(setConfig);
  }, []);

  useEffect(() => {
    queueMicrotask(() => void loadAccount());
    const refresh = () => void loadAccount();
    window.addEventListener('agam:profile-updated', refresh);
    return () => window.removeEventListener('agam:profile-updated', refresh);
  }, [loadAccount]);

  if (window.location.pathname === '/restablecer') {
    return (
      <PageShell>
        <ResetPassword />
      </PageShell>
    );
  }

  const displayName =
    account?.profile?.artisticName ?? account?.profile?.publicName ?? session?.user.name ?? '';
  const readyAccount = Boolean(session && accountLoaded && account?.profile);

  return (
    <PageShell>
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-5 py-6 sm:px-8 lg:px-12">
        <a href="#inicio" className="flex items-center gap-3" aria-label="AGAM, inicio">
          <img src="/agam-mark.svg" alt="" className="h-10 w-10" aria-hidden="true" />
          <span>
            <span className="block text-sm font-black tracking-[0.28em] text-white">AGAM</span>
            <span className="block text-[0.62rem] uppercase tracking-[0.2em] text-zinc-500">
              Arena musical
            </span>
          </span>
        </a>
        {session && (
          <div className="flex items-center gap-2">
            <NotificationCenter />
            <SessionMenu displayName={displayName} onSignedOut={refetch} />
          </div>
        )}
      </header>

      <main
        id="inicio"
        className="relative z-10 mx-auto max-w-7xl px-5 pb-20 pt-10 sm:px-8 lg:px-12 lg:pt-16"
      >
        {!session && (
          <section className="grid items-center gap-12 lg:grid-cols-[1.18fr_.82fr] lg:gap-16">
            <div>
              <p className="eyebrow flex items-center gap-3">
                <span className="h-px w-8 bg-fuchsia-400" />
                Tu voz ya tiene identidad
              </p>
              <h1 className="mt-6 max-w-4xl text-5xl font-black leading-[0.96] tracking-[-0.055em] text-white sm:text-7xl lg:text-[5.25rem]">
                Entra a la arena.
                <span className="gradient-text block">Tu canción encuentra rival.</span>
              </h1>
              <p className="mt-7 max-w-2xl text-base leading-7 text-zinc-400 sm:text-lg sm:leading-8">
                Publica un reto abierto o desafía a un artista registrado. Cada reproducción
                permanece en el enlace oficial de quien creó la canción.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  href="#arena"
                  className="rounded-full bg-white px-6 py-3 text-sm font-bold text-zinc-950 transition hover:bg-fuchsia-200"
                >
                  Explorar batallas
                </a>
                <a
                  href="#ranking"
                  className="rounded-full border border-white/10 px-6 py-3 text-sm font-bold text-zinc-300 transition hover:border-fuchsia-200/40 hover:text-white"
                >
                  Ver ranking
                </a>
              </div>
            </div>

            {isPending || !config ? (
              <div className="glass-panel rounded-[2rem] p-8 text-sm text-zinc-400">
                Preparando acceso seguro…
              </div>
            ) : (
              <AuthPanel config={config} onAuthenticated={refetch} />
            )}
          </section>
        )}

        {session && !accountLoaded && (
          <div className="glass-panel rounded-[2rem] p-8 text-sm text-zinc-400">
            Preparando tu arena…
          </div>
        )}

        {session && accountLoaded && !account?.profile && (
          <ProfilePanel onCompleted={loadAccount} />
        )}

        {(!session || readyAccount) && <BattlesArena authenticated={Boolean(session)} />}
        {(!session || readyAccount) && <MonthlyCupArena authenticated={Boolean(session)} />}
        {(!session || readyAccount) && <RankingBoard />}
        {readyAccount && <SponsorshipAdmin authenticated />}
        {readyAccount && <AdminTrustCenter authenticated />}

        {(!session || readyAccount) && (
          <section className="mt-12 rounded-[2rem] border border-amber-300/15 bg-amber-300/[0.04] p-6 sm:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-200">
              Regla invariable de Copa
            </p>
            <p className="mt-3 max-w-4xl text-sm leading-6 text-zinc-400">
              En cada batalla de Copa, ambos contrincantes usarán el mismo archivo instrumental. La
              descarga mostrará “Pista creada por: [productor]” y los créditos deberán permanecer en
              la publicación final.
            </p>
          </section>
        )}
      </main>

      <footer className="relative z-10 border-t border-white/8 px-5 py-7 text-center text-xs tracking-wide text-zinc-600">
        Arena Musical - AGAM Inc. - Todos los derechos Reservados.
      </footer>
    </PageShell>
  );
}

function SessionMenu({
  displayName,
  onSignedOut,
}: {
  displayName: string;
  onSignedOut: () => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  async function signOut() {
    setSigningOut(true);
    await authClient.signOut();
    await onSignedOut();
    setSigningOut(false);
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="max-w-44 truncate rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-zinc-200 transition hover:border-fuchsia-300/30 hover:text-white"
        aria-expanded={open}
      >
        {displayName || 'Mi cuenta'}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 rounded-2xl border border-white/10 bg-zinc-950/95 p-2 shadow-2xl backdrop-blur">
          <button
            type="button"
            onClick={signOut}
            disabled={signingOut}
            className="w-full rounded-xl px-3 py-2 text-left text-sm text-zinc-300 transition hover:bg-white/5 hover:text-white disabled:opacity-50"
          >
            {signingOut ? 'Cerrando…' : 'Cerrar sesión'}
          </button>
        </div>
      )}
    </div>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen overflow-hidden bg-zinc-950 text-zinc-100">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      {children}
    </div>
  );
}
